import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { portalApi, setPortalToken } from "@/lib/portalApi";
import { BRAND } from "@/lib/brandingConfig";
import { Loader2, AlertCircle } from "lucide-react";

/**
 * /portal/login?token=<raw> — exchanges the magic-link token for a JWT,
 * stores it in `hw_portal_token`, and redirects to /portal.
 *
 * The Care Seeker should never see this screen for more than ~1 second
 * unless something is wrong with the link.
 */
export default function PortalLogin() {
  const { search } = useLocation();
  const navigate = useNavigate();
  const [error, setError] = useState("");

  useEffect(() => {
    const token = new URLSearchParams(search).get("token") || "";
    if (!token) {
      setError("That link is missing a token. Ask your Care Navigator to resend the email.");
      return;
    }
    portalApi
      .post("/portal/login", { token })
      .then(({ data }) => {
        setPortalToken(data.access_token);
        navigate("/portal", { replace: true });
      })
      .catch((e) => {
        setError(e?.response?.data?.detail || "We couldn't sign you in. Ask your Care Navigator to send a new link.");
      });
    // eslint-disable-next-line
  }, [search]);

  return (
    <div className="min-h-screen bg-[#F9F8F6] flex items-center justify-center p-6" data-testid="portal-login-page">
      <div className="bg-white border border-[#E5E5E0] rounded-2xl shadow-sm max-w-md w-full p-8 text-center">
        <img
          src={BRAND.logo}
          alt={BRAND.logoAlt}
          className="h-14 w-auto mx-auto mb-4"
          data-testid="portal-login-brand-logo"
        />
        <h1 className="font-display text-2xl font-semibold text-[#0F172A]">HarborWell Connect</h1>
        <p className="text-sm text-[#334155] mt-2">Care Seeker Portal</p>
        {!error ? (
          <div className="mt-6 inline-flex items-center gap-2 text-[#1E3A8A]">
            <Loader2 className="w-4 h-4 animate-spin" /> Signing you in…
          </div>
        ) : (
          <div className="mt-6 bg-[#FEE2E2] border border-[#FCA5A5] rounded-xl p-4 text-left">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-[#991B1B] flex-shrink-0 mt-0.5" />
              <div>
                <div className="text-sm font-medium text-[#991B1B]">We couldn't sign you in</div>
                <div className="text-sm text-[#334155] mt-1">{error}</div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
